'use client'
import { useCallback, useMemo, useRef, useState } from 'react'
import type { AssistantMessage, AssistantResponse, AssistantState, AssistantContextParams } from './types'

function uuid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

function getCookie(name: string) {
  if (typeof document === 'undefined') return ''
  const v = document.cookie.split('; ').find(row => row.startsWith(name + '='))
  return v ? decodeURIComponent(v.split('=')[1]) : ''
}

export function useAssistant(initial?: Partial<AssistantState>) {
  const [open, setOpen] = useState<boolean>(initial?.open ?? false)
  const [messages, setMessages] = useState<AssistantMessage[]>(initial?.messages ?? [])
  const [sending, setSending] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [context, setContext] = useState<AssistantContextParams | undefined>(initial?.context)
  const listRef = useRef<HTMLDivElement | null>(null)

  const scrollToBottom = useCallback(() => {
    const el = listRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [])

  const toggle = useCallback(() => setOpen(v => !v), [])

  const setOpenPanel = useCallback((v: boolean) => setOpen(v), [])

  const sendMessage = useCallback(
    async (message: string) => {
      setError(undefined)
      const userMsg: AssistantMessage = { id: uuid(), role: 'user', content: message }
      setMessages(prev => [...prev, userMsg])
      setSending(true)
      try {
        const csrf = getCookie('duo_csrf')
        const res = await fetch('/api/agent', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrf
          },
          body: JSON.stringify({ message, context }),
          credentials: 'include'
        })
        if (res.status === 401) {
          setError('Please log in to access account features.')
          setSending(false)
          return
        }
        if (res.status === 402) {
          setError('This action is not available for your account right now.')
          setSending(false)
          return
        }
        if (res.status === 403) {
          const body = await res.json().catch(() => null)
          const code = (body as any)?.error
          if (code === 'PLAN_RESTRICTED') {
            setError('This action is restricted for your account.')
          } else if (code === 'CSRF_INVALID') {
            setError('Session expired. Please refresh and try again.')
          } else {
            setError('This action is restricted for your account.')
          }
          setSending(false)
          return
        }
        const data: AssistantResponse = await res.json()
        const assistantMsg: AssistantMessage = {
          id: uuid(),
          role: 'assistant',
          content: data.reply ?? '',
          actions: data.actions ?? []
        }
        const nextResumeId = assistantMsg.actions?.find(
          a => (a.type === 'generate_resume' || a.type === 'optimize_resume') && typeof a.payload?.resumeId === 'string'
        )?.payload?.resumeId as string | undefined
        if (nextResumeId) {
          setContext(prev => ({ ...(prev ?? {}), resumeId: nextResumeId }))
        }
        setMessages(prev => [...prev, assistantMsg])
      } catch {
        setError('Network error')
      } finally {
        setSending(false)
        setTimeout(scrollToBottom, 16)
      }
    },
    [context, scrollToBottom]
  )

  const value: AssistantState & {
    setContext: (c?: AssistantContextParams) => void
    setOpenPanel: (v: boolean) => void
    toggle: () => void
    sendMessage: (message: string) => Promise<void>
    listRef: React.MutableRefObject<HTMLDivElement | null>
  } = useMemo(
    () => ({
      open,
      messages,
      sending,
      error,
      context,
      setContext,
      setOpenPanel,
      toggle,
      sendMessage,
      listRef
    }),
    [open, messages, sending, error, context, setContext, setOpenPanel, toggle, sendMessage]
  )

  return value
}
